import { useEffect, useState } from "react";
import { Users } from "lucide-react";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { listMembers } from "@/api/projects";
import type { ProjectMember } from "@/api/types";

/** Sentinel value for the "whole team" option — distinct from any
 *  member UUID. Exported so the tabs seed their state with it. */
export const ALL_MEMBERS = "all";

interface CallUserFilterProps {
  slug: string;
  /** Selected operator — `ALL_MEMBERS` for the whole team, or a
   *  member UUID. */
  value: string;
  onChange: (userId: string) => void;
}

/** Operator picker shared by the Call Center Stats and History tabs.
 *
 *  Only rendered for users with `call_center.view_team` — the caller
 *  gates it on `canViewCallCenterTeam`, so the members request never
 *  fires for users who can only see their own calls. */
export function CallUserFilter({ slug, value, onChange }: CallUserFilterProps) {
  const [members, setMembers] = useState<ProjectMember[]>([]);

  useEffect(() => {
    let cancelled = false;
    listMembers(slug)
      .then((res) => {
        if (!cancelled) setMembers(res);
      })
      .catch(() => {
        if (!cancelled) setMembers([]);
      });
    return () => {
      cancelled = true;
    };
  }, [slug]);

  return (
    <div className="flex items-center gap-1.5">
      <Users className="h-4 w-4 shrink-0 text-slate-400" />
      <Select value={value} onValueChange={onChange}>
        <SelectTrigger
          className="h-9 w-48 text-sm"
          aria-label="Filter by team member"
        >
          <SelectValue />
        </SelectTrigger>
        <SelectContent>
          <SelectItem value={ALL_MEMBERS}>All team members</SelectItem>
          {members.map((m) => (
            <SelectItem key={m.id} value={m.id}>
              {m.full_name}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
    </div>
  );
}
